import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { CLOUD_DATA_HYDRATED_EVENT } from '../lib/cloud/reconcile';

export interface PlanTier {
  targetPrice: string;
  alloc: string;
}

export interface BuyPlanState {
  coinId: string;
  coinName: string;
  coinSymbol: string;
  coinImage: string;
  budget: string;
  futurePrice: string;
  tiers: PlanTier[];
  currencyCode: string;
}

export interface SellPlanState {
  coinId: string;
  coinName: string;
  coinSymbol: string;
  coinImage: string;
  coinsHeld: string;
  avgBuyPrice: string;
  tiers: PlanTier[];
  currencyCode: string;
}

export interface TaxHarvestState {
  coinId: string;
  coinName: string;
  coinSymbol: string;
  coinImage: string;
  coinsSold: string;
  buyPrice: string;        // AUD
  sellPrice: string;       // AUD
  purchaseDate: string;    // ISO date string
  bracketIdx: number;
}

interface StrategyContextValue {
  buyPlan: BuyPlanState | null;
  updateBuyPlan: (plan: BuyPlanState | null) => void;
  sellPlan: SellPlanState | null;
  updateSellPlan: (plan: SellPlanState | null) => void;
  taxHarvest: TaxHarvestState | null;
  updateTaxHarvest: (plan: TaxHarvestState | null) => void;
}

const BUY_KEY  = 'mycryptoplanner_buy_plan';
const SELL_KEY = 'mycryptoplanner_sell_plan';
const TAX_KEY  = 'mycryptoplanner_tax_harvest';

function loadFromLS<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch { return null; }
}

const StrategyContext = createContext<StrategyContextValue>({
  buyPlan: null,    updateBuyPlan:    () => {},
  sellPlan: null,   updateSellPlan:   () => {},
  taxHarvest: null, updateTaxHarvest: () => {},
});

export function StrategyProvider({ children }: { children: React.ReactNode }) {
  const [buyPlan,    setBuyPlan]    = useState<BuyPlanState    | null>(() => loadFromLS(BUY_KEY));
  const [sellPlan,   setSellPlan]   = useState<SellPlanState   | null>(() => loadFromLS(SELL_KEY));
  const [taxHarvest, setTaxHarvest] = useState<TaxHarvestState | null>(() => loadFromLS(TAX_KEY));

  // Drafts initialise above the auth render-gate, so re-read them from localStorage
  // once the login reconcile has hydrated cloud values into it.
  useEffect(() => {
    const reread = () => {
      setBuyPlan(loadFromLS<BuyPlanState>(BUY_KEY));
      setSellPlan(loadFromLS<SellPlanState>(SELL_KEY));
      setTaxHarvest(loadFromLS<TaxHarvestState>(TAX_KEY));
    };
    window.addEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
    return () => window.removeEventListener(CLOUD_DATA_HYDRATED_EVENT, reread);
  }, []);

  const updateBuyPlan = useCallback((plan: BuyPlanState | null) => {
    setBuyPlan(plan);
    try {
      if (plan) localStorage.setItem(BUY_KEY,  JSON.stringify(plan));
      else      localStorage.removeItem(BUY_KEY);
    } catch { /* ignore */ }
  }, []);

  const updateSellPlan = useCallback((plan: SellPlanState | null) => {
    setSellPlan(plan);
    try {
      if (plan) localStorage.setItem(SELL_KEY,  JSON.stringify(plan));
      else      localStorage.removeItem(SELL_KEY);
    } catch { /* ignore */ }
  }, []);

  const updateTaxHarvest = useCallback((plan: TaxHarvestState | null) => {
    setTaxHarvest(plan);
    try {
      if (plan) localStorage.setItem(TAX_KEY,  JSON.stringify(plan));
      else      localStorage.removeItem(TAX_KEY);
    } catch { /* ignore */ }
  }, []);

  return (
    <StrategyContext.Provider value={{
      buyPlan, updateBuyPlan,
      sellPlan, updateSellPlan,
      taxHarvest, updateTaxHarvest,
    }}>
      {children}
    </StrategyContext.Provider>
  );
}

export function useStrategy(): StrategyContextValue {
  return useContext(StrategyContext);
}
